import React, { useState } from 'react';
import { Check, DollarSign, Leaf } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SaleToggle from './SaleToggle';
import ImageDropZone from './ImageDropZone';

// Quick Edit Product Modal
const QuickEditProduct = ({ product, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: product.name || '',
    price: product.price || 0,
    sale_price: product.sale_price || null,
    inventory_quantity: product.inventory_quantity || 0,
    description: product.description || '',
    category: product.category || 'plants',
    display_order: product.display_order ?? '',
    care_guide: product.care_guide || {
      light: '',
      co2: '',
      growth_rate: '',
      placement: '',
      difficulty: ''
    }
  });
  const [images, setImages] = useState([
    product.image_url,
    ...(product.image_gallery?.map(g => g.url) || [])
  ].filter(Boolean));

  const handleCareGuideChange = (field, value) => {
    setFormData({
      ...formData,
      care_guide: {
        ...formData.care_guide,
        [field]: value
      }
    });
  };

  const handleSave = () => {
    const [mainImage, ...galleryImages] = images;
    // Only include care_guide if at least one field has a value
    const careGuideHasValues = Object.values(formData.care_guide).some(v => v && v.trim());
    onSave({
      ...formData,
      display_order: formData.display_order === '' ? null : parseInt(formData.display_order),
      image_url: mainImage || '',
      image_gallery: galleryImages.map(url => ({ url, alt_text: '' })),
      care_guide: careGuideHasValues ? formData.care_guide : null
    });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Product Name</Label>
          <Input 
            value={formData.name} 
            onChange={e => setFormData({...formData, name: e.target.value})}
          />
        </div>
        <div>
          <Label>Category</Label>
          <Select value={formData.category} onValueChange={v => setFormData({...formData, category: v})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="plants">Plants</SelectItem>
              <SelectItem value="bundles">Bundles</SelectItem>
              <SelectItem value="invertebrates">Invertebrates</SelectItem>
              <SelectItem value="fish">Fish</SelectItem>
              <SelectItem value="hardscape">Hardscape</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Description</Label>
        <Textarea 
          value={formData.description} 
          onChange={e => setFormData({...formData, description: e.target.value})}
          rows={2}
        />
      </div>

      {/* Price Section with Sale Toggle */}
      <div className="bg-secondary/30 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <DollarSign className="w-4 h-4 text-primary" />
          <Label className="text-base font-semibold">Pricing</Label>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Regular Price ($)</Label>
            <Input 
              type="number" 
              step="0.01"
              value={formData.price} 
              onChange={e => setFormData({...formData, price: parseFloat(e.target.value) || 0})}
            />
          </div>
          <div>
            <Label>Stock Quantity</Label>
            <Input 
              type="number" 
              value={formData.inventory_quantity} 
              onChange={e => setFormData({...formData, inventory_quantity: parseInt(e.target.value) || 0})}
            />
          </div>
          <div>
            <Label>Display Order</Label>
            <Input 
              type="number" 
              value={formData.display_order} 
              onChange={e => setFormData({...formData, display_order: e.target.value})}
              placeholder="Auto (alphabetical)"
            />
            <p className="text-xs text-muted-foreground mt-1">Lower = shows first. Leave empty for alphabetical.</p>
          </div>
        </div>

        <SaleToggle 
          price={formData.price}
          salePrice={formData.sale_price}
          onSaleChange={(newSalePrice) => setFormData({...formData, sale_price: newSalePrice})}
        />
      </div>

      <ImageDropZone 
        images={images}
        onImageAdd={setImages}
        onImageRemove={setImages}
        label="Product Images (drag & drop URLs)"
      />

      {/* Care Guide Section */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Leaf className="w-4 h-4 text-green-600" />
          <Label className="text-base font-semibold text-green-800">Plant Care Guide (Optional)</Label>
        </div>
        <p className="text-xs text-green-600 mb-3">Fill these out to display a care card on the product page</p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-sm">Light Requirements</Label>
            <Select 
              value={formData.care_guide.light || ''} 
              onValueChange={v => handleCareGuideChange('light', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select light level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Low">Low</SelectItem>
                <SelectItem value="Low - Medium">Low - Medium</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Medium - High">Medium - High</SelectItem>
                <SelectItem value="High">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">CO2 Requirements</Label>
            <Select 
              value={formData.care_guide.co2 || ''} 
              onValueChange={v => handleCareGuideChange('co2', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select CO2 level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="None">None</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Recommended">Recommended</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Growth Rate</Label>
            <Select 
              value={formData.care_guide.growth_rate || ''} 
              onValueChange={v => handleCareGuideChange('growth_rate', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select growth rate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Slow">Slow</SelectItem>
                <SelectItem value="Moderate">Moderate</SelectItem>
                <SelectItem value="Fast">Fast</SelectItem>
                <SelectItem value="Very Fast">Very Fast</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Placement</Label>
            <Select 
              value={formData.care_guide.placement || ''} 
              onValueChange={v => handleCareGuideChange('placement', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select placement" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Foreground">Foreground</SelectItem>
                <SelectItem value="Midground">Midground</SelectItem>
                <SelectItem value="Background">Background</SelectItem>
                <SelectItem value="Floating">Floating</SelectItem>
                <SelectItem value="Attached">Attached (wood/rock)</SelectItem>
                <SelectItem value="Any">Any</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-sm">Difficulty Level</Label>
            <Select 
              value={formData.care_guide.difficulty || ''} 
              onValueChange={v => handleCareGuideChange('difficulty', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select difficulty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Beginner">Beginner</SelectItem>
                <SelectItem value="Easy">Easy</SelectItem>
                <SelectItem value="Moderate">Moderate</SelectItem>
                <SelectItem value="Advanced">Advanced</SelectItem>
                <SelectItem value="Expert">Expert</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex gap-2 pt-4">
        <Button onClick={handleSave} className="flex-1">
          <Check className="w-4 h-4 mr-2" /> Save Changes
        </Button>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
};

export default QuickEditProduct;
